import React, { createContext, FC, useEffect, useState } from 'react';
import { usePageNavigate } from '../hooks/usePageNavigate';
import { isAllTabInUse } from '../selectors/tabs-selectors';
import {
  AppTemplate,
  DevelopmentTool,
} from '@wix/ambassador-devcenter-apps-v1-app-template/types';
import { useQueryParam } from '../hooks/useQueryParam';
import { QueryParams } from '../constants/QueryParams';
import { isValidAppliedFilter } from '../utils/urlParams-utils';
import { useTemplatesPageBi } from '../hooks/useTemplatesPageBi';
import { NonTypeTab, TemplateTab } from '../types/TemplateTabs';

export interface ITemplatesTabsContext {
  activeTabId: TemplateTab;
  onTabClick: (
    tabId: TemplateTab,
    mainGridRef: React.RefObject<HTMLDivElement>,
    shouldScrollIntoView: boolean,
  ) => void;
  shouldDisplayTemplateByTab: (template: AppTemplate) => boolean;
}

export const TemplatesTabsContext = createContext<ITemplatesTabsContext>({
  activeTabId: NonTypeTab.ALL,
  onTabClick: () => {},
  shouldDisplayTemplateByTab: () => true,
});
export const TemplatesTabsContextProvider: FC = ({ children }) => {
  const { appliedFilter, setParamAccordingToTab } = usePageNavigate();
  const { removeParam } = useQueryParam();
  const [activeTabId, setActiveTabId] = useState<TemplateTab>(
    isValidAppliedFilter(appliedFilter) ? appliedFilter : NonTypeTab.ALL,
  );
  const isAllTab = isAllTabInUse(activeTabId);
  const { reportClickOnTab } = useTemplatesPageBi();

  const onTabClick = (
    tabId: TemplateTab,
    mainGridRef: React.RefObject<HTMLDivElement>,
    shouldScrollIntoView: boolean,
  ) => {
    reportClickOnTab(tabId as string);
    setActiveTabId(tabId);
    setParamAccordingToTab(tabId);
    shouldScrollIntoView &&
      mainGridRef.current?.scrollIntoView({ behavior: 'smooth' });
  };
  const shouldDisplayTemplateByTab = (template: AppTemplate): boolean =>
    isAllTab ||
    !!template?.developmentTools?.includes(activeTabId as DevelopmentTool);

  useEffect(() => {
    const isValidFilter = appliedFilter && isValidAppliedFilter(appliedFilter);
    if (!isValidFilter) {
      removeParam(QueryParams.FILTER);
    }
  }, [removeParam, appliedFilter]);

  return (
    <TemplatesTabsContext.Provider
      value={{ activeTabId, onTabClick, shouldDisplayTemplateByTab }}
    >
      {children}
    </TemplatesTabsContext.Provider>
  );
};
